// CoachContact — the Pro-gated contact UI (Contactable References).
//
// Three states, decided ENTIRELY by which keys the server shipped
// (server-side response shaping — the client never sees values it isn't
// allowed to render):
//   1. `contact` present  → viewer is a Pro coach → real mailto/wa.me links
//   2. `contact_locked`   → blurred PLACEHOLDER strings + lock + upsell.
//      The blur is CSS on fake text ("•••@••••••.com") — the real values
//      never reached the browser in any form.
//   3. neither / locked=false with no values → render nothing.
//
// `viewerIsCoach` upgrades the locked label into a /pricing link (a coach
// seeing the locked state is by definition a FREE coach — a Pro coach
// would have received values). Agents/anon get the plain label.

import { Link } from 'react-router-dom'
import { Lock, Mail, Phone, Copy, Check } from 'lucide-react'
import { useState } from 'react'

export interface CoachContactValues {
  email: string | null
  whatsapp: string | null
}

// wa.me wants digits only (no +, spaces, or dashes). Display keeps the
// value exactly as the coach entered it.
export function waHref(whatsapp: string): string {
  return `https://wa.me/${whatsapp.replace(/\D/g, '')}`
}

/**
 * A wa.me link is only trustworthy when the stored value carries an explicit
 * country code — the '+'-prefixed format C1 introduced.
 *
 * waHref strips non-digits, so a legacy bare national number does not build a
 * BROKEN link, it builds a confidently WRONG one: Kaillum's stored
 * '4379613497' yields wa.me/4379613497, which reads '43' as the country.
 *
 * Same rule as splitWhatsapp — only a '+' value is unambiguous — and the same
 * codebase norm as never summing a NULL height as 0 or storing partial-coverage
 * eFG as 0: do not derive an artifact from data we have declared
 * uninterpretable. Non-conforming numbers still render and stay copyable, and
 * the link comes back by itself once a country code is saved.
 */
export function whatsappIsLinkable(whatsapp: string | null | undefined): boolean {
  return (whatsapp ?? '').trim().startsWith('+')
}

const PLACEHOLDER_EMAIL = '•••@••••••.com'
const PLACEHOLDER_PHONE = '+•• ••• ••• •••'

function LockedTeaser({
  viewerIsCoach,
  compact,
}: {
  viewerIsCoach: boolean
  compact?: boolean
}) {
  const label = 'Available with Coach Pro'
  return (
    <div className={compact ? 'flex items-center gap-2 flex-wrap' : 'space-y-1.5'}>
      <span
        aria-hidden="true"
        className="blur-[3px] select-none text-sm text-hoop-text-2"
      >
        {PLACEHOLDER_EMAIL}
      </span>
      {!compact && (
        <span
          aria-hidden="true"
          className="block blur-[3px] select-none text-sm text-hoop-text-2"
        >
          {PLACEHOLDER_PHONE}
        </span>
      )}
      <span className="inline-flex items-center gap-1 text-[11px] font-medium text-hoop-text-3">
        <Lock className="w-3 h-3" />
        {viewerIsCoach ? (
          <Link to="/pricing" className="text-hoop-accent underline hover:no-underline">
            {label}
          </Link>
        ) : (
          label
        )}
      </span>
    </div>
  )
}

function ContactLinks({
  contact,
  compact,
}: {
  contact: CoachContactValues
  compact?: boolean
}) {
  // C4 — keyed by field, not a bare boolean. With one shared flag, copying the
  // phone would light up "Copied" on the email too.
  const [copied, setCopied] = useState<'email' | 'phone' | null>(null)

  const handleCopy = async (value: string, field: 'email' | 'phone') => {
    // navigator.clipboard is undefined in insecure contexts
    if (typeof navigator.clipboard === 'undefined') {
      return
    }
    try {
      await navigator.clipboard.writeText(value)
      setCopied(field)
      setTimeout(() => setCopied(null), 2000)
    } catch {
      // Silent fail on permission denied or other clipboard errors
    }
  }

  if (contact.email === null && contact.whatsapp === null) return null
  return (
    <div className={compact ? 'flex items-center gap-3 flex-wrap' : 'space-y-1.5'}>
      {contact.email && (
        <div className={(compact ? '' : 'block ') + 'min-w-0'}>
          {/*
            C6 — was `break-all`, which chopped namd@cedarparkventures.ca into
            "…ventures.c" / "a" mid-word. `break-words` only breaks a token that
            genuinely cannot fit, and the actions moved to their own line below
            so the address gets the card's full width instead of sharing the row.
          */}
          <a
            href={`https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(contact.email)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-start gap-1.5 text-sm text-hoop-accent hover:underline break-words min-w-0"
          >
            <Mail className="w-3.5 h-3.5 shrink-0 mt-0.5" />
            <span className="min-w-0">{contact.email}</span>
          </a>
          <div className="flex items-center gap-2 mt-1">
            <a
              href={`mailto:${contact.email}`}
              className="text-xs text-hoop-text-2 hover:text-hoop-accent hover:underline"
            >
              (mailto)
            </a>
            {typeof navigator.clipboard !== 'undefined' && (
              <button
                type="button"
                onClick={() => handleCopy(contact.email!, 'email')}
                className="inline-flex items-center gap-1 text-xs text-hoop-text-2 hover:text-hoop-accent"
                title="Copy email address"
              >
                {copied === 'email' ? (
                  <>
                    <Check className="w-3 h-3" />
                    Copied
                  </>
                ) : (
                  <>
                    <Copy className="w-3 h-3" />
                    Copy
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      )}
      {contact.whatsapp && (
        <div className={(compact ? '' : 'block ') + 'min-w-0'}>
          {/*
            The wa.me link PRE-DATES this pass — it shipped with the contact
            card in e6a7e72. It is now GATED on the '+'-prefixed storage
            format: an ungated link was live on Kaillum's public profile built
            from '4379613497'. See whatsappIsLinkable for why that is worse
            than a dead link. Non-conforming values render as plain text and
            stay copyable.
          */}
          {whatsappIsLinkable(contact.whatsapp) ? (
            <a
              href={waHref(contact.whatsapp)}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 text-sm text-hoop-accent hover:underline break-words min-w-0"
            >
              <Phone className="w-3.5 h-3.5 shrink-0" />
              <span className="min-w-0">{contact.whatsapp}</span>
            </a>
          ) : (
            <div className="flex items-center gap-1.5 text-sm text-hoop-text break-words min-w-0">
              <Phone className="w-3.5 h-3.5 shrink-0" />
              <span className="min-w-0">{contact.whatsapp}</span>
            </div>
          )}
          {/* C4 — same control, styling and feedback as the email's Copy. */}
          {typeof navigator.clipboard !== 'undefined' && (
            <div className="flex items-center gap-2 mt-1">
              <button
                type="button"
                onClick={() => handleCopy(contact.whatsapp!, 'phone')}
                className="inline-flex items-center gap-1 text-xs text-hoop-text-2 hover:text-hoop-accent"
                title="Copy phone number"
              >
                {copied === 'phone' ? (
                  <>
                    <Check className="w-3 h-3" />
                    Copied
                  </>
                ) : (
                  <>
                    <Copy className="w-3 h-3" />
                    Copy
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// Full sidebar card for /coaches/:slug.
export function CoachContactCard({
  contact,
  contactLocked,
  viewerIsCoach,
}: {
  contact?: CoachContactValues
  contactLocked?: boolean
  viewerIsCoach: boolean
}) {
  // A Pro viewer on a coach with no contact set receives
  // `contact: {email: null, whatsapp: null}` — a truthy OBJECT. Testing
  // the object alone let that through and rendered a bare "Contact"
  // heading with nothing under it (found on prod 07-28). Test the values.
  const hasValues =
    contact != null && (contact.email !== null || contact.whatsapp !== null)
  if (!hasValues && !contactLocked) return null
  return (
    <section className="bg-hoop-surface border border-hoop-border rounded-lg p-6">
      <h2 className="text-sm font-semibold text-hoop-text uppercase tracking-wider mb-3">
        Contact
      </h2>
      {hasValues ? (
        <ContactLinks contact={contact!} />
      ) : (
        <LockedTeaser viewerIsCoach={viewerIsCoach} />
      )}
    </section>
  )
}

// Compact per-reference line for CoachReferencesCard.
export function CoachContactLine({
  contact,
  contactLocked,
  viewerIsCoach,
}: {
  contact?: CoachContactValues
  contactLocked?: boolean
  viewerIsCoach: boolean
}) {
  if (contact && (contact.email !== null || contact.whatsapp !== null)) {
    return (
      <div className="mt-2">
        <ContactLinks contact={contact} compact />
      </div>
    )
  }
  if (contactLocked) {
    return (
      <div className="mt-2">
        <LockedTeaser viewerIsCoach={viewerIsCoach} compact />
      </div>
    )
  }
  return null
}
